import React, { useMemo } from "react";
import {
  AccountTreeOutlined,
  CalendarMonthOutlined,
  DateRangeOutlined,
  Inventory2Outlined,
  ListAltOutlined,
  MemoryOutlined,
  VerifiedUserOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { translate } from "../../../../translate/translate";
import { formatter } from "../../../../utils/formatter-utils";
import { MaterialStatusSummary } from "../../../../models/general/material-status";
import { useAuth } from "../../../../contexts/auth-context";

interface MaterialStatusDialogProps {
  open: boolean;
  onClose: () => void;
  summary: MaterialStatusSummary;
  error?: string | null;
  canConfirm?: boolean;
  isGenerating?: boolean;
  onConfirm?: () => void;
  analysisId?: string | number | null;
  responsibleUser?: string | null;
}

interface SummaryItemProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

const unavailableValue = () => translate.t("material_status_data_unavailable");

const formatCount = (value?: number | null) => {
  if (value === undefined || value === null) return unavailableValue();
  return new Intl.NumberFormat(translate.language || "pt-BR").format(value);
};

const SummaryItem = ({ icon, label, value }: SummaryItemProps) => (
  <Stack direction="row" alignItems="center" spacing={1.25}>
    <Stack sx={{ color: "text.secondary", flexShrink: 0 }}>{icon}</Stack>
    <Stack spacing={0.125}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", fontSize: 12, lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.3 }}>
        {value}
      </Typography>
    </Stack>
  </Stack>
);

interface InformationPanelProps {
  title: string;
  titleIcon?: React.ReactNode;
  children: React.ReactNode;
}

const InformationPanel = ({ title, titleIcon, children }: InformationPanelProps) => (
  <Paper
    variant="outlined"
    elevation={0}
    sx={{
      borderRadius: 1,
      p: 1.5,
      height: "100%",
      boxSizing: "border-box",
      backgroundColor: "background.paper",
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
      {titleIcon && <Stack sx={{ color: "text.secondary", flexShrink: 0 }}>{titleIcon}</Stack>}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.25 }}>
        {title}
      </Typography>
    </Stack>
    {children}
  </Paper>
);

const MaterialStatusDialog = ({
  open,
  onClose,
  summary,
  error,
  canConfirm = false,
  isGenerating = false,
  onConfirm,
  analysisId,
  responsibleUser,
}: MaterialStatusDialogProps) => {
  const { user } = useAuth();
  const {
    analysis_date: analysisDate,
    horizon_weeks: horizonWeeks,
    included_boms: includedBoms,
    models_count: modelsCount,
    items_count: itemsCount,
    ready_to_generate: readyToGenerate,
  } = summary;
  const formattedDate = analysisDate ? formatter.formatDate(analysisDate) : unavailableValue();
  const formattedHorizon =
    horizonWeeks === undefined || horizonWeeks === null
      ? unavailableValue()
      : translate.t("material_status_weeks", { count: horizonWeeks });
  const openedAt = useMemo(() => open ? new Date().toISOString() : null, [open]);
  const displayedGenerationDate = openedAt
    ? formatter.formatDateTime(openedAt)
    : unavailableValue();
  const displayedResponsibleUser = responsibleUser || user?.username || unavailableValue();
  const displayedAnalysis = analysisId !== undefined && analysisId !== null && analysisId !== ""
    ? String(analysisId)
    : unavailableValue();
  const displayedModelsItems = translate.t("material_status_models_items_value", {
    models: formatCount(modelsCount),
    items: formatCount(itemsCount),
  });

  return (
    <Dialog
      open={open}
      onClose={isGenerating ? undefined : onClose}
      disableEscapeKeyDown={isGenerating}
      maxWidth={false}
      aria-labelledby="material-status-dialog-title"
      aria-describedby="material-status-dialog-description"
      PaperProps={{
        sx: {
          borderRadius: 1.5,
          width: 640,
          maxWidth: "calc(100vw - 32px)",
          m: 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        id="material-status-dialog-title"
        sx={{
          px: 2,
          py: 1.5,
          pr: 7,
          backgroundColor: "primary.main",
          color: "primary.contrastText",
        }}
      >
        <Typography
          component="span"
          sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.5 }}
          style={{ color: "#FFFFFF" }}
        >
          {translate.t("generate_material_status")}
        </Typography>
        <IconButton
          aria-label={translate.t("close")}
          onClick={onClose}
          disabled={isGenerating}
          sx={{
            position: "absolute",
            right: 8,
            top: 5,
            color: "primary.contrastText",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ px: 3, pb: 2, overflowX: "hidden" }}
        style={{ paddingTop: 12 }}
      >
        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography
          id="material-status-dialog-description"
          variant="subtitle2"
          sx={{ mb: 2, fontWeight: 700, fontSize: 13, lineHeight: 1.25 }}
        >
          {translate.t("material_status_subtitle")}
        </Typography>

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <InformationPanel
              title={translate.t("material_status_current_analysis")}
            >
              <Stack spacing={1.25}>
                <SummaryItem
                  icon={<CalendarMonthOutlined fontSize="small" />}
                  label={translate.t("material_status_analysis_date")}
                  value={formattedDate}
                />
                <SummaryItem
                  icon={<DateRangeOutlined fontSize="small" />}
                  label={translate.t("material_status_horizon")}
                  value={formattedHorizon}
                />
                <SummaryItem
                  icon={<AccountTreeOutlined fontSize="small" />}
                  label={translate.t("material_status_boms_included")}
                  value={formatCount(includedBoms)}
                />
              </Stack>
            </InformationPanel>
          </Grid>

          <Grid item xs={12} md={6}>
            <InformationPanel
              title={translate.t("material_status_available_data")}
            >
              <Stack spacing={1.25}>
                <SummaryItem
                  icon={<MemoryOutlined fontSize="small" />}
                  label={translate.t("material_status_models_boards")}
                  value={formatCount(modelsCount)}
                />
                <SummaryItem
                  icon={<Inventory2Outlined fontSize="small" />}
                  label={translate.t("material_status_gds_items")}
                  value={formatCount(itemsCount)}
                />
                <SummaryItem
                  icon={<DateRangeOutlined fontSize="small" />}
                  label={translate.t("weeks")}
                  value={formatCount(horizonWeeks)}
                />
              </Stack>
            </InformationPanel>
          </Grid>

          <Grid item xs={12}>
            <InformationPanel
              title={translate.t("material_status_identification")}
              titleIcon={<VerifiedUserOutlined fontSize="small" />}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                {translate.t("material_status_history_intro")}
              </Typography>
              <Box
                component="dl"
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "210px minmax(0, 1fr)" },
                  rowGap: 0.5,
                  columnGap: 2,
                  m: 0,
                  width: "auto !important",
                  "& dt": { color: "text.secondary" },
                  "& dd": { m: 0, fontWeight: 700 },
                }}
              >
                <Typography component="dt" variant="body2">
                  {translate.t("material_status_generation_date")}
                </Typography>
                <Typography component="dd" variant="body2">
                  {displayedGenerationDate}
                </Typography>
                <Typography component="dt" variant="body2">
                  {translate.t("material_status_responsible_user")}
                </Typography>
                <Typography component="dd" variant="body2">{displayedResponsibleUser}</Typography>
                <Typography component="dt" variant="body2">
                  {translate.t("material_status_linked_analysis")}
                </Typography>
                <Typography component="dd" variant="body2">{displayedAnalysis}</Typography>
                <Typography component="dt" variant="body2">
                  {translate.t("material_status_models_items_quantity")}
                </Typography>
                <Typography component="dd" variant="body2">{displayedModelsItems}</Typography>
              </Box>
            </InformationPanel>
          </Grid>

          <Grid item xs={12}>
            <InformationPanel
              title={translate.t("material_status_preview")}
              titleIcon={<ListAltOutlined fontSize="small" />}
            >
              <Box
                component="dl"
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "210px minmax(0, 1fr)" },
                  rowGap: 0.5,
                  columnGap: 2,
                  m: 0,
                  width: "auto !important",
                  "& dt": { color: "text.secondary" },
                  "& dd": { m: 0, fontWeight: 700 },
                }}
              >
                <Typography component="dt" variant="body2">
                  {translate.t("material_status_analysis_date")}
                </Typography>
                <Typography component="dd" variant="body2">{formattedDate}</Typography>
                <Typography component="dt" variant="body2">
                  {translate.t("material_status_horizon")}
                </Typography>
                <Typography component="dd" variant="body2">{formattedHorizon}</Typography>
                <Typography component="dt" variant="body2">
                  {translate.t("material_status_models_boards_quantity")}
                </Typography>
                <Typography component="dd" variant="body2">{formatCount(modelsCount)}</Typography>
                <Typography component="dt" variant="body2">
                  {translate.t("material_status_status")}
                </Typography>
                <Box component="dd" sx={{ m: 0, width: "auto !important" }}>
                  <Chip
                    size="small"
                    label={
                      readyToGenerate
                        ? translate.t("material_status_ready")
                        : translate.t("material_status_integration_unavailable")
                    }
                    color={readyToGenerate ? "success" : "default"}
                    variant={readyToGenerate ? "filled" : "outlined"}
                    sx={{ fontWeight: 700 }}
                  />
                </Box>
              </Box>
            </InformationPanel>
          </Grid>

          <Grid item xs={12}>
            <Alert severity="info" variant="outlined">
              {translate.t("material_status_static_notice")}
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider", gap: 0.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={isGenerating} sx={{ width: 110 }}>
          {translate.t("cancel")}
        </Button>
        <Tooltip
          title={
            canConfirm
              ? ""
              : translate.t("material_status_not_ready")
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              onClick={onConfirm}
              disabled={!canConfirm || isGenerating}
              startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ width: 192 }}
            >
              {translate.t("material_status_confirm")}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default MaterialStatusDialog;
